<script setup lang="ts">
/**
 * Global network incident banner.
 *
 * Shown as soon as one store reports a transport failure. It is informative,
 * never blocking: the data already on screen stays visible and readable, and a
 * single explicit action replays the failed read. Server failures are not shown
 * here; they belong to the state card of the screen that owns the request,
 * together with their request identifier.
 */
import { computed } from 'vue';
import { HzButton } from '@horizon/ui';
import type { ErreurHorizon } from '@horizon/api-client';

const props = withDefaults(
  defineProps<{
    /** Network error reported by a store, or null when the link is healthy. */
    erreur: ErreurHorizon | null;
    /** True while the retry is in flight. */
    enCours?: boolean;
  }>(),
  { enCours: false },
);

const emit = defineEmits<{
  reessayer: [];
}>();

const message = computed(() =>
  props.erreur === null
    ? ''
    : props.erreur.messageUtilisateur ||
      'Connexion interrompue : vérifiez votre réseau puis réessayez.',
);
</script>

<template>
  <div
    v-if="props.erreur !== null"
    class="hz-bandeau hz-bandeau--reseau bandeau"
    role="alert"
  >
    <svg
      class="bandeau__icone"
      viewBox="0 0 18 18"
      aria-hidden="true"
      focusable="false"
    >
      <circle
        cx="9"
        cy="9"
        r="7.4"
        fill="none"
        stroke="currentColor"
        stroke-width="1.6"
      />
      <line
        x1="9"
        y1="5"
        x2="9"
        y2="9.8"
        stroke="currentColor"
        stroke-width="1.6"
        stroke-linecap="round"
      />
      <circle
        cx="9"
        cy="12.6"
        r="0.9"
        fill="currentColor"
      />
    </svg>
    <p class="bandeau__texte">
      {{ message }}
    </p>
    <HzButton
      variant="secondary"
      size="sm"
      :loading="props.enCours"
      @click="emit('reessayer')"
    >
      Réessayer
    </HzButton>
  </div>
</template>

<style scoped>
.bandeau {
  width: 100%;
}

.bandeau__icone {
  flex: none;
  width: 18px;
  height: 18px;
}

.bandeau__texte {
  flex: 1 1 240px;
  min-width: 0;
  font-size: var(--hz-fs-md);
  font-weight: 600;
}
</style>
